/**
 * @module app.shared
 */

import { bindingMode, autoinject, processContent, computedFrom } from 'aurelia-framework';
import flatpickr from 'flatpickr';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { bindable } from 'aurelia-typed-observable-plugin';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { DateTimeValue, DateValue } from 'app/shared/lib/date-time';
import { updateMaterializeInputLabel } from 'app/shared/lib/materialize/dom-utilities';
import { getKey, Key } from 'app/shared/lib/keys';
import { microTask, defer } from 'app/shared/lib/decorators';
import { moveRulesToHelperSlot } from 'app/shared/elements/ess-rule';
import { IInput } from 'app/shared/elements/forms/input-base';
import { stop } from 'app/shared/lib/noop';
import { deferEvent } from 'app/shared/lib/fire-events';
import { BaseOptions, DateOption } from 'flatpickr/dist/types/options';
import { guid } from 'app/shared/lib/guid';
import { I18N } from 'aurelia-i18n';
import { format } from 'date-fns';

@autoinject()
@processContent(moveRulesToHelperSlot)
export class EssTimeCustomElement implements IInput {

    protected id: guid = guid();

    @bindable.stringNull({ defaultBindingMode: bindingMode.twoWay })
    public value: string | null = null;

    @bindable.string()
    public testName: string = '';

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public minTime: string | null = null;

    @bindable.stringNull({ changeHandler: 'initializePicker' })
    public maxTime: string | null = null;

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public use24hour: boolean = false;

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public useSeconds: boolean = false;

    @bindable.stringNull()
    public label: string | null = null;

    @bindable.stringNull()
    public requiredValidatorLabel: string | null = null;

    @bindable.stringNull()
    public placeholder: string | null = null;

    @bindable.string()
    public autocomplete: string = 'off';

    @bindable.booleanAttr()
    public required: boolean = false;

    @bindable.booleanAttr()
    public readonly: boolean = false;

    @bindable.booleanAttr()
    public disabled: boolean = false;

    protected instance: flatpickr.Instance | null = null;
    protected isChanging: boolean = false;
    protected pickerId: string | null = null;

    protected minTimeCompare: DateTimeValue | null = null;
    protected maxTimeCompare: DateTimeValue | null = null;

    // refs
    protected readonly input!: HTMLInputElement;
    protected readonly element!: HTMLElement;
    protected readonly labelElement!: HTMLLabelElement;

    protected deferEvent = deferEvent;

    constructor(private readonly formatter: FormatProvider,
        private readonly i18n: I18N,
        private readonly env: EnvironmentService) { }

    protected attached(): void {
        this.initializePicker();
        this.placeholderChanged();
    }

    protected detached(): void {
        this.destroyPicker();
    }

    protected destroyPicker(): void {
        if(this.instance) {
            this.instance.destroy();
            this.instance = null;
        }
    }

    protected initializePicker(): void {

        // INFO [Matt] Called by the change handlers before element is attached,
        // so we need to check that the element is attached.
        if(!this.element) {
            return;
        }

        const onChange = this.onChange.bind(this);
        const onClose = this.onClose.bind(this);
        const onOpen = this.onOpen.bind(this);
        const formatDate = this.formatDate.bind(this);
        const options: Partial<BaseOptions> = {
            allowInput: true,
            mode: 'time',
            clickOpens: false,
            enableSeconds: this.useSeconds,
            time_24hr: this.use24hour,
            wrap: true,
            onOpen,
            onChange,
            onClose: [onChange, onClose],
            disableMobile: true,
            ariaDateFormat: 'aria',
            dateFormat: 'H-i-S K',
            formatDate
        };

        // matches e.g. 5:00:00, 14:00:00, 16:00
        const timePattern = /\d{1,2}:\d{2}(?::\d{2})?/
        if(this.minTime) {
            // if minTime is an ISO 8601 string, adjust to user's time zone
            if(this.minTime.length > 8) this.minTime = this.formatTime(this.minTime);
            const timeMatch = this.minTime.match(timePattern);
            options.minTime = timeMatch?.length ? timeMatch[0] : '';
            this.minTimeCompare = this.getTimeCompare(options.minTime);
        }
        if(this.maxTime) {
            // if maxTime is an ISO 8601 string, adjust to user's time zone
            if(this.maxTime.length > 8) this.maxTime = this.formatTime(this.maxTime);
            const timeMatch = this.maxTime.match(timePattern);
            options.maxTime = timeMatch?.length ? timeMatch[0] : '';
            this.maxTimeCompare = this.getTimeCompare(options.maxTime);
        }

        // safeguard against invalid min/max values
        if(this.minTimeCompare && this.maxTimeCompare && this.minTimeCompare.isAfter(this.maxTimeCompare)) {
            options.minTime = options.maxTime = undefined;
            this.minTimeCompare = this.maxTimeCompare = null;
            throw new Error('Overlapping min and maxTime bindings');
        }
        this.instance = flatpickr(this.element, options);

        if(this.instance && this.instance.timeContainer) {
            this.pickerId = this.instance.timeContainer.getAttribute('id');
            if(!this.pickerId) {
                this.pickerId = `${this.id}-picker`;
                this.instance.timeContainer.setAttribute('id', this.pickerId);
            }
        }

        this.updateValue();
    }

    protected valueChanged(newValue: string | null, oldValue: string | null): void {
        if(this.instance && (newValue !== oldValue)) {
            this.updateValue();
        }
    }

    protected handleFocusOut(): true {

        // INFO [Matt] Essentially copying the the internal logic of flatpickr on
        // enter key press to the case where focus is lost, e.g. tabs. See
        // https://github.com/flatpickr/flatpickr/blob/v4.6.2/src/index.ts#L1593.
        if(this.instance) {
            this.instance.setDate(
                this.instance._input.value,
                true,
                this.instance.config.dateFormat
            );
            this.instance.close();
        }
        return true;
    }

    @computedFrom('label', 'requiredValidatorLabel')
    protected get requiredValueMessage() {
        return this.requiredValidatorLabel || this.i18n.tr('ui:validation:required', { what: this.i18n.tr(this.label || 'ui:labels.value') });
    }

    private updateValue(): void {
        if(!this.instance) return;
        let dateTimeValue = new DateTimeValue(this.value || '');
        if(!this.isChanging) {
            if(dateTimeValue.isValid()) {
                const nativeDate = DateTimeValue.toNativeDate(dateTimeValue, this.env.displayTimeZone);
                this.instance.setDate(nativeDate, false); // don't fire change
            } else {
                this.instance.clear(false); // don't fire change
            }

            this.queueUpdateLabel();
        } else if(dateTimeValue.value && (this.minTimeCompare || this.maxTimeCompare)) {
            let isChangeMade = false;
            // enforce min/max even if user uses keyboard input instead of picker
            if(this.minTimeCompare && this.minTimeCompare.isAfter(dateTimeValue)) {
                // If value is below min time, then set time to min time
                dateTimeValue = new DateTimeValue(this.minTimeCompare.toString());
                isChangeMade = true;
            } else if(this.maxTimeCompare && this.maxTimeCompare.isBefore(dateTimeValue)) {
                // If value is above max time, then set time to max time
                dateTimeValue = new DateTimeValue(this.maxTimeCompare.toString());
                isChangeMade = true;
            }

            if(dateTimeValue.isValid() && isChangeMade) {
                const nativeDate = DateTimeValue.toNativeDate(dateTimeValue, this.env.displayTimeZone);
                this.instance.setDate(nativeDate, true, this.instance.config.dateFormat);
            }
        }
    }

    protected open(): void {
        if(this.instance && !this.readonly) {
            this.instance.open();
        }
    }

    protected clearValue(): void {
        if(this.readonly) { return; }
        if(this.instance) {
            this.instance.clear(false); // don't fire change
            this.instance.close();
        }
        this.value = null;
    }

    protected handleClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if(this.readonly || this.disabled || target.hasAttribute('data-clear')) return;
        this.open();
    }

    protected handleKeyDown(event: KeyboardEvent): boolean {
        // improving the lives of keyboard users
        const key = getKey(event);
        if(key === Key.Delete) {
            this.clearValue();
            return false;
        }
        if(key === Key.Enter) {
            this.open();
        }
        return true;
    }

    private onChange(selectedDates: Date[]): void {
        if(!selectedDates.length) {
            this.synchronizeValue(null);
        }
        else {
            const value = DateTimeValue.fromNativeDate(selectedDates[0], this.env.displayTimeZone).toJSON();
            this.synchronizeValue(value);
        }

        this.queueUpdateLabel();
    }

    @microTask
    private synchronizeValue(value: string | null): void {
        // bindable values update asynchronously, so run this in a microtask
        try {
            // prevent infinite recursion
            this.isChanging = true;
            this.value = value;
        }
        finally {
            this.isChanging = false;
        }
    }

    protected placeholderChanged(): void {
        this.queueUpdateLabel();
    }

    @defer
    private queueUpdateLabel(): void {
        updateMaterializeInputLabel(this.input, this.labelElement);
    }

    protected stop(event: Event): true {
        if(this.readonly) {
            stop(event);
        }

        return true;
    }

    private onOpen(): void {
        if(this.instance) {
            // move label out of way when popup opens
            if(!this.value && !this.placeholder) {
                this.input.setAttribute('placeholder', ' ');
            }
            this.instance.hourElement!.focus();
        }
    }

    private onClose(): void {
        if(!this.placeholder) {
            // return label to original place
            this.input.setAttribute('placeholder', '');
        }
    }

    private formatDate(date: Date, format: string): string {
        const value = DateTimeValue.fromNativeDate(date, this.env.displayTimeZone);
        if(format === 'aria') {
            return this.formatter.formatDate(value, { format: 'aria' }) || '';
        }
        return this.formatter.formatTime(value) || '';
    }

    private formatTime(time: string, format: string = 'default'): string {
        return this.formatter.formatTime(time, {
            format,
            timeZone: this.env.displayTimeZone,
            time24: true
        }) || '';
    }

    private getTimeCompare(time: string): DateTimeValue | null {
        const timeSubString = time.split(":");
        const hour = Number.parseInt(timeSubString[0]) || 0;
        const minute = Number.parseInt(timeSubString[1]) || 0;
        const second = Number.parseInt(timeSubString[2]) || 0;
        const dateValue = new DateValue(format(Date.now(), 'yyyy-MM-dd')).toDateTime({ timeZone: this.env.displayTimeZone, hour, minute, second, millisecond: 0 });
        return dateValue.isValid() ? dateValue : null;
    }

}
